import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  BookOpen,
  CheckCircle2,
  ChevronRight,
  X,
  Lightbulb,
  HelpCircle,
  FileText,
  MessageCircle,
  Calendar,
  Shield,
  Video,
  ClipboardList,
  Upload,
  UserCheck,
  Plane
} from "lucide-react";
import { Button } from "../ui/button";

// Requisitos para completar cada paso
const STEP_REQUIREMENTS = {
  2: { total: 3, label: "documentos" },
  3: { total: 12, label: "secciones" }
};

const QuickStartGuide = ({ currentStep = 1, documentsCount = 0, ds160Progress = 0 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasSeenGuide, setHasSeenGuide] = useState(false);
  
  // Calcular progreso por paso
  const getStepProgress = (stepNumber) => {
    if (stepNumber === 2) {
      const progress = Math.min((documentsCount / STEP_REQUIREMENTS[2].total) * 100, 100);
      return { progress, current: documentsCount, total: STEP_REQUIREMENTS[2].total, label: STEP_REQUIREMENTS[2].label };
    }
    if (stepNumber === 3) {
      const progress = Math.min((ds160Progress / STEP_REQUIREMENTS[3].total) * 100, 100);
      return { progress, current: ds160Progress, total: STEP_REQUIREMENTS[3].total, label: STEP_REQUIREMENTS[3].label };
    }
    return null;
  };
  
  useEffect(() => {
    const seen = localStorage.getItem('hasSeenQuickGuide');
    if (seen === 'true') {
      setHasSeenGuide(true);
    } else {
      // Show guide automatically on first visit after a short delay
      const timer = setTimeout(() => {
        setIsOpen(true);
        localStorage.setItem('hasSeenQuickGuide', 'true');
        setHasSeenGuide(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);
  
  // Alineado con VISA_STEPS del backend (6 pasos del Camino al Éxito)
  const steps = [
    { number: 1, title: "Cuenta creada", description: "Tu cuenta está activa y lista", icon: UserCheck, route: null },
    { number: 2, title: "Subir documentos", description: "Pasaporte, foto y comprobantes", icon: Upload, route: "/step/documents" },
    { number: 3, title: "Completar DS-160", description: "Formulario de solicitud de visa", icon: FileText, route: "/step/ds160" },
    { number: 4, title: "Verificación", description: "Llamada con tu asesor", icon: Video, route: "/step/verification" },
    { number: 5, title: "DS-160 Oficial", description: "Envío al consulado", icon: ClipboardList, route: null },
    { number: 6, title: "¡Entrevista!", description: "Tu cita en el consulado", icon: Plane, route: "/interview-questions" }
  ];
  
  return (
    <>
      {/* Floating button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        onClick={() => setIsOpen(true)}
        className="fixed bottom-24 left-6 z-40 bg-emerald-600 text-white p-3 rounded-full shadow-lg hover:bg-emerald-700 transition-colors flex items-center gap-2"
        title="Ver guía paso a paso"
        data-testid="quick-guide-btn"
      >
        <HelpCircle className="w-5 h-5" />
        {!hasSeenGuide && (
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
        )}
      </motion.button>
      
      {/* Modal */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/50 z-50"
            />
            
            {/* Modal content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:max-w-2xl md:w-full bg-white rounded-2xl shadow-2xl z-50 overflow-hidden max-h-[90vh] overflow-y-auto"
              data-testid="quick-guide-modal"
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <BookOpen className="w-6 h-6" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold">Tu Camino al Éxito</h2>
                      <p className="text-emerald-100 text-sm">6 pasos para obtener tu visa</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Progress */}
                <div className="mt-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Progreso actual</span>
                    <span className="font-medium">Paso {currentStep} de 6</span>
                  </div>
                  <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${((currentStep) / 6) * 100}%` }}
                      className="h-full bg-white"
                    />
                  </div>
                </div>
              </div>
              
              {/* Steps */}
              <div className="p-6">
                <div className="space-y-3">
                  {steps.map((step) => {
                    const Icon = step.icon;
                    const isCompleted = step.number < currentStep;
                    const isCurrent = step.number === currentStep;
                    
                    // Pasos 2 y 3 son paralelos - ambos disponibles si el usuario está en cualquiera de ellos
                    const isParallelStep = step.number === 2 || step.number === 3;
                    const userInParallelPhase = currentStep === 2 || currentStep === 3;
                    const isAvailable = isParallelStep && userInParallelPhase && !isCompleted && !isCurrent;
                    
                    // Obtener progreso para pasos 2 y 3
                    const stepProgress = getStepProgress(step.number);
                    const showProgress = stepProgress && (isCurrent || isAvailable) && !isCompleted;
                    
                    return (
                      <div
                        key={step.number}
                        className={`p-3 rounded-xl border transition-all ${
                          isCurrent 
                            ? "bg-emerald-50 border-emerald-300 shadow-sm" 
                            : isAvailable
                            ? "bg-amber-50 border-amber-200 shadow-sm"
                            : isCompleted
                            ? "bg-gray-50 border-gray-200"
                            : "border-gray-100"
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          {/* Step number/check */}
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isCompleted 
                              ? "bg-emerald-500 text-white" 
                              : isCurrent 
                              ? "bg-emerald-600 text-white"
                              : isAvailable
                              ? "bg-amber-500 text-white"
                              : "bg-gray-100 text-gray-400"
                          }`}>
                            {isCompleted ? (
                              <CheckCircle2 className="w-5 h-5" />
                            ) : (
                              <span className="font-bold">{step.number}</span>
                            )}
                          </div>
                          
                          {/* Step info */}
                          <div className="flex-1 min-w-0">
                            <h4 className={`font-medium ${
                              isCurrent ? "text-emerald-800" : isAvailable ? "text-amber-700" : isCompleted ? "text-gray-600" : "text-gray-800"
                            }`}>
                              {step.title}
                              {isAvailable && <span className="ml-2 text-xs text-amber-600 font-normal">(También disponible)</span>}
                            </h4>
                            <p className="text-sm text-gray-500 truncate">{step.description}</p>
                          </div>
                          
                          {/* Icon */}
                          <Icon className={`w-5 h-5 flex-shrink-0 ${
                            isCurrent ? "text-emerald-600" : isAvailable ? "text-amber-500" : isCompleted ? "text-emerald-500" : "text-gray-300"
                          }`} />
                          
                          {/* Current indicator */}
                          {isCurrent && (
                            <span className="px-2 py-1 bg-emerald-600 text-white text-xs rounded-full">
                              Actual
                            </span>
                          )}
                          {isAvailable && (
                            <span className="px-2 py-1 bg-amber-500 text-white text-xs rounded-full">
                              Ir
                            </span>
                          )}
                        </div>
                        
                        {/* Progress bar for steps 2 and 3 */}
                        {showProgress && (
                          <div className="mt-3 ml-14">
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className={isCurrent ? "text-emerald-700" : "text-amber-700"}>
                                {stepProgress.current}/{stepProgress.total} {stepProgress.label}
                              </span>
                              <span className={isCurrent ? "text-emerald-600" : "text-amber-600"}>
                                {Math.round(stepProgress.progress)}%
                              </span>
                            </div>
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full transition-all duration-500 ${
                                  stepProgress.progress >= 100 
                                    ? "bg-emerald-500" 
                                    : isCurrent 
                                    ? "bg-emerald-500" 
                                    : "bg-amber-500"
                                }`}
                                style={{ width: `${stepProgress.progress}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {/* Tip */}
                <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <span className="font-medium text-blue-800">Tip: </span>
                      <span className="text-blue-700">
                        Los pasos de <strong>Documentos</strong> y <strong>DS-160</strong> se pueden trabajar en paralelo. ¡Completa ambos para avanzar más rápido!
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="mt-6 flex gap-3">
                  <Link to="/guia/cliente" className="flex-1">
                    <Button variant="outline" className="w-full">
                      <BookOpen className="w-4 h-4 mr-2" />
                      Guía completa
                    </Button>
                  </Link>
                  <Button onClick={() => setIsOpen(false)} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                    Entendido
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default QuickStartGuide;
